import type { ReactNode } from 'react';
import type { AssignedRiskApplicationDetailViewModel } from 'src/api/types/AssignedRiskApplicationDetailViewModel';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import TableContainer from '@mui/material/TableContainer';

import { MESSAGES } from 'src/constants/messages';
import { useAssignedRiskApplicationApplicationDetails } from 'src/api/hooks/useAssignedRiskApplicationQueries';

import { formatDate } from '../../shared/utils/policyUtils';

type ApplicationDetailsProps = Readonly<{ assignedRiskAppId: number }>;
const applicationMessages = MESSAGES.ASSIGNED_RISK_APPLICATION.APPLICATION_DETAILS;

function display(value: string | null | undefined): string {
  return value ?? '';
}

function displayIndicator(value: boolean | null | undefined): string {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return '';
}

function SupportingDetails({ detail }: Readonly<{ detail?: AssignedRiskApplicationDetailViewModel | null }>) {
  const values = [
    ['Carrier', detail?.disputeCarrierName],
    ['Insured', detail?.disputeInsuredName],
    ['Policy number', detail?.disputePolicyNumber],
    ['Explanation', detail?.disputePremiumExplanation],
  ];

  return (
    <Grid container spacing={0.5} sx={{ mt: 1 }}>
      {values.map(([label, value]) => (
        <Grid key={label} size={{ xs: 12 }}>
          <Typography component="span" variant="body2" color="text.secondary">
            {label}:{' '}
          </Typography>
          <Typography component="span" variant="subtitle2">
            {value}
          </Typography>
        </Grid>
      ))}
    </Grid>
  );
}

const qualifyingChangeItems = applicationMessages.QUALIFYING_CHANGE_ITEMS;

function IndicatorValue({ value, children }: Readonly<{ value: boolean | null | undefined; children?: ReactNode }>) {
  return (
    <>
      <Typography variant="subtitle2">{displayIndicator(value)}</Typography>
      {children}
    </>
  );
}

export default function ApplicationDetails({ assignedRiskAppId }: ApplicationDetailsProps) {
  const { data, isLoading, isError } = useAssignedRiskApplicationApplicationDetails(assignedRiskAppId);
  const details = data?.data;
  const coverageHistory = details?.coverageHistory;

  if (isError) {
    return <Alert severity="error" data-testid="application-details-error">{applicationMessages.LOAD_ERROR}</Alert>;
  }

  if (isLoading) {
    return (
      <Card sx={{ mt: 3 }} data-testid="application-details-loading">
        <CardContent sx={{ p: 3 }}>
          <Skeleton variant="text" width={220} height={32} />
          <Grid container spacing={3} sx={{ mt: 2 }}>
            {Array.from({ length: 5 }, (_, index) => (
              <Grid key={index} size={{ xs: 12, sm: 6, md: 4 }}>
                <Skeleton variant="text" width="40%" />
                <Skeleton variant="text" width="80%" />
              </Grid>
            ))}
          </Grid>
          <Skeleton variant="rectangular" height={260} sx={{ mt: 3 }} />
        </CardContent>
      </Card>
    );
  }

  if (!details) {
    return <Alert severity="warning" data-testid="application-details-not-found">{applicationMessages.NOT_FOUND}</Alert>;
  }

  const rows: Array<{ id: string; label: ReactNode; value: ReactNode }> = [
    {
      id: 'previous-coverage-nc',
      label: applicationMessages.QUESTIONS.PREVIOUS_COVERAGE_NC,
      value: <IndicatorValue value={coverageHistory?.previousCoverageNcIndicator}>{coverageHistory?.noPreviousCoverageReasonDescription ? <Typography variant="body2" sx={{ color: 'text.secondary' }}>{coverageHistory.noPreviousCoverageReasonDescription}</Typography> : null}</IndicatorValue>,
    },
    { id: 'previous-coverage-other-state', label: applicationMessages.QUESTIONS.PREVIOUS_COVERAGE_OTHER_STATE, value: <IndicatorValue value={coverageHistory?.previousCoverageOtherStatesIndicator} /> },
    {
      id: 'unpaid-premiums-dispute',
      label: applicationMessages.QUESTIONS.UNPAID_PREMIUMS_DISPUTE,
      value: <IndicatorValue value={coverageHistory?.premiumDisputeIndicator}>
        <SupportingDetails detail={coverageHistory} />
      </IndicatorValue>,
    },
    {
      id: 'qualifying-change',
      label: <>
        <Typography component="p" variant="body2">{applicationMessages.QUESTIONS.QUALIFYING_CHANGE}</Typography>
        <Box component="ul" sx={{ pl: 2.5, my: 0, listStyleType: 'disc', listStylePosition: 'outside' }}>
          {qualifyingChangeItems.map((item) => <Box component="li" key={item} sx={{ pl: 0.5 }}>{item}</Box>)}
        </Box>
      </>,
      value: <IndicatorValue value={coverageHistory?.nameOwnerChangeIndicator} />,
    },
    { id: 'uses-subcontractors', label: applicationMessages.QUESTIONS.USES_SUBCONTRACTORS, value: <IndicatorValue value={coverageHistory?.subcontractorsIndicator} /> },
    { id: 'leases-workers', label: applicationMessages.QUESTIONS.LEASES_WORKERS, value: <IndicatorValue value={coverageHistory?.leasedWorkersCoverageIndicator} /> },
    { id: 'trucking-classifications', label: applicationMessages.QUESTIONS.TRUCKING_CLASSIFICATIONS, value: <IndicatorValue value={coverageHistory?.truckClassIndicator} /> },
    {
      id: 'ops-other-states',
      label: applicationMessages.QUESTIONS.OPERATIONS_OTHER_STATES,
      value: <IndicatorValue value={coverageHistory?.opsAdditionalStateIndicator}><Typography variant="body2" sx={{ color: 'text.secondary' }}>{display(coverageHistory?.opsStatesDescription)}</Typography></IndicatorValue>,
    },
    { id: 'listed-states', label: applicationMessages.QUESTIONS.LISTED_STATES, value: <IndicatorValue value={coverageHistory?.additionalStateCoverageIndicator} /> },
  ];

  const metadataFields = [
    { id: 'manage-ar-id', label: 'ManageAR ID', value: display(details.manageArId) },
    { id: 'initiated-date', label: 'Initiated date', value: formatDate(details.initiatedDate) },
    { id: 'last-updated-date', label: 'Last updated date', value: formatDate(details.lastUpdatedDate) },
    { id: 'requested-effective-date', label: 'Requested effective date', value: formatDate(details.requestedEffectiveDate) },
    { id: 'coverage-effective-date', label: 'Coverage effective date', value: formatDate(details.coverageEffectiveDate) },
  ];

  return (
    <Card sx={{ mt: 3, borderRadius: 2, boxShadow: 'var(--customShadows-card)' }} data-testid="application-details-card">
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h5">{applicationMessages.TITLE}</Typography>
        <Grid container spacing={3} sx={{ mt: 2 }} data-testid="application-details-metadata">
          {metadataFields.map(({ id, label, value }) => (
            <Grid key={id} size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{label}</Typography>
              <Typography variant="subtitle2" data-testid={`application-details-${id}`}>{value}</Typography>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 3, borderStyle: 'dashed' }} />
        <Typography variant="h5">{applicationMessages.AGENT_NOTES_TITLE}</Typography>
        <Box data-testid="application-details-agent-notes" sx={{ mt: 1 }}>
          {(details.agentNotes ?? []).map((note) => (
            <Typography key={note.assignedRiskAppAgentNoteId} variant="body2" data-testid="application-details-agent-note">
              {note.assignedRiskAgentNoteDescription ?? ''}
            </Typography>
          ))}
        </Box>

        <Divider sx={{ my: 3, borderStyle: 'dashed' }} />
        <Typography variant="h5">{applicationMessages.COVERAGE_HISTORY_TITLE}</Typography>
        <TableContainer sx={{ mt: 2, border: 1, borderColor: 'divider', borderRadius: 2, overflowX: 'auto' }}>
          <Table data-testid="application-details-coverage-history-table" sx={{ tableLayout: 'fixed' }}>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell component="th" scope="row" sx={{ width: '50%', verticalAlign: 'top' }}>{row.label}</TableCell>
                  <TableCell sx={{ verticalAlign: 'top', overflowWrap: 'anywhere' }}>{row.value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}